import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { initSentry, markChunkReloadInFlight } from './lib/sentry';
import { resolveStaffFenceRedirect } from './utils/publicOriginGuard';

// Before render so it catches errors during the initial mount. No-op in
// offline dev / when no DSN is configured.
initSentry();

// Stale-chunk recovery: every deploy renames the hashed route chunks, so a tab
// opened before a deploy 404s ("Failed to fetch dynamically imported module")
// on its next lazy-route navigation. Vite fires `vite:preloadError` for that;
// one reload fetches the fresh index.html + chunk graph. The sessionStorage
// stamp keeps a genuinely-broken load (e.g. offline) from reload-looping —
// at most one automatic reload per minute per tab.
window.addEventListener('vite:preloadError', (event) => {
  const RELOAD_STAMP_KEY = 'wss-chunk-reload-at';
  const last = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) ?? 0);
  if (Date.now() - last < 60_000) return; // fall through to the error boundary
  sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  // Suppressing the throw makes Vite's preload helper RESOLVE the dynamic
  // import with `undefined`, so React.lazy reads `.default` off undefined and
  // the ErrorBoundary reports a phantom TypeError before the (async) reload
  // commits. Flag the window so Sentry drops it — see lib/sentryFilters.ts.
  markChunkReloadInFlight();
  event.preventDefault(); // suppress the throw — the reload supersedes it
  window.location.reload();
});

// Origin fence (per-company public domains): entity domains serve PUBLIC
// pages only — a staff path there bounces to the staff origin before anything
// renders. WSS and unknown origins (localhost/emulator/preview) never fence.
const fenceTarget = resolveStaffFenceRedirect(
  window.location.origin,
  window.location.pathname,
  window.location.search,
);
if (fenceTarget) {
  window.location.replace(fenceTarget);
} else {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
